import {
    backToClientFormsThunk,
    continueToFormsInvitationPetsThunk,
    initFormsInvitationThunk,
    submitFormsInvitationThunk
} from 'actions/formsInvitation/FormsInvitationActions';
import {
    calculateTotalSteps,
    convertFormsInvitationResponse,
    customerHasQuestions,
    petsHaveQuestions
} from 'actions/formsInvitation/FormsInvitationConverter';
import { heroGirlKissingDog } from 'assets/images';
import { ClientFormPage } from 'components/ClientForm/ClientFormPage/ClientFormPage';
import { LinkExpired } from 'components/ClientForm/LinkExpired/LinkExpired';
import { PetFormPage } from 'components/ClientForm/PetFormPage/PetFormPage';
import { FormsInvitationSuccess } from 'components/ClientForm/Success/Success';
import { Header } from 'components/Layout/Header/Header';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { LoadingOverlay } from 'components/UI/LoadingOverlay/LoadingOverlay';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    getFormsInvitationCurrentView,
    getFormsInvitationData,
    getFormsInvitationError,
    getFormsInvitationLoading
} from 'reducers/formsInvitation/FormsInvitationSelectors';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';

export const FormsInvitationPage: React.FC = () => {
    const dispatch = useDispatch();
    const queryParams = useParams<{ slug: string; token: string }>();

    const isLoading = useSelector(getFormsInvitationLoading);
    const currentView = useSelector(getFormsInvitationCurrentView);
    const data = useSelector(getFormsInvitationData);
    const error = useSelector(getFormsInvitationError);
    const marketplace = useSelector(getMarketplace);

    React.useEffect(() => {
        if (queryParams.slug && queryParams.token) {
            dispatch(initFormsInvitationThunk(queryParams.slug, queryParams.token));
        }
        // eslint-disable-next-line
    }, []);

    // Auto-skip logic
    React.useEffect(() => {
        if (!data) return;

        const hasCustomerQuestions = customerHasQuestions(data.customer);
        const hasPetQuestions = petsHaveQuestions(data.pets);

        // Skip customer view if no customer questions
        if (currentView === 'client' && !hasCustomerQuestions) {
            if (hasPetQuestions) {
                // Go to pets if they have questions
                dispatch(continueToFormsInvitationPetsThunk());
            } else {
                // Skip to success if no pets questions either
                dispatch(submitFormsInvitationThunk());
            }
        }

        // Skip pets view if no pet questions
        if (currentView === 'pets' && !hasPetQuestions) {
            // Go directly to success
            dispatch(submitFormsInvitationThunk());
        }
        // eslint-disable-next-line
    }, [data, currentView]);

    // Show LinkExpired component if error code is 410
    if (error && error.code === 410) {
        return <LinkExpired />;
    }

    if (isLoading || !data) {
        return <LoadingOverlay />;
    }

    const handleContinue = () => {
        // Check if pets have questions before navigating
        if (petsHaveQuestions(data.pets)) {
            dispatch(continueToFormsInvitationPetsThunk());
        } else {
            // Skip pets and go directly to success
            dispatch(submitFormsInvitationThunk());
        }
    };

    const handleBack = () => {
        dispatch(backToClientFormsThunk());
    };

    const handleSubmit = () => {
        dispatch(submitFormsInvitationThunk());
    };

    // Convert backend data to component format
    const convertedData = convertFormsInvitationResponse(data);
    const { invitation, customer, pets, petForms } = convertedData;

    // Calculate total steps dynamically (considering both customer and pets)
    const hasCustomerQuestions = customerHasQuestions(data.customer);
    const hasPetQuestions = petsHaveQuestions(data.pets);
    const totalSteps = calculateTotalSteps(data.customer, data.pets);

    // Success view
    if (currentView === 'success') {
        const successStep = totalSteps;
        const progress = (successStep / totalSteps) * 100;
        return (
            <TwoColumnsLayout
                header={<Header logo={marketplace.logo} progress={progress} />}
                leftColumn={<FormsInvitationSuccess />}
                rightColumnImage={heroGirlKissingDog}
            />
        );
    }

    // Pets view
    if (currentView === 'pets') {
        // Only render if pets have questions (otherwise auto-skipped)
        if (!hasPetQuestions) {
            return <LoadingOverlay />; // Will be auto-skipped by useEffect
        }

        // Calculate step number based on whether customer questions exist
        const petsStep = hasCustomerQuestions ? 2 : 1;
        const showBackButton = hasCustomerQuestions; // Only show back if came from customer forms

        return (
            <PetFormPage
                currentStep={petsStep}
                totalSteps={totalSteps}
                notifyAddPet={invitation.notifyAddPet}
                notifyAddQuestion={invitation.notifyAddQuestion}
                pets={pets}
                petForms={petForms}
                onBack={showBackButton ? handleBack : undefined}
                onSubmit={handleSubmit}
            />
        );
    }

    // Client view (default)
    // Only render if customer has questions (otherwise auto-skipped)
    return (
        <ClientFormPage
            currentStep={1}
            totalSteps={totalSteps}
            notifyAddQuestion={invitation.notifyAddQuestion}
            clientName={customer.fullName}
            clientPhone={String(customer.phone || '')}
            clientEmail={customer.email}
            forms={customer.forms}
            isLastStep={!hasPetQuestions}
            onContinue={handleContinue}
        />
    );
};
